import httpClient from "@plugins/http";
import { ApiResponse } from "@apis/api.ts";
import {
  CommentResponse,
  CommentPaginationResponse,
  CommentSummaryLatestResponse,
  CommentSummaryResponse,
} from "@plugins/comment/types.ts";

interface FetchCommentSummaryLatestParams {
  resourceUuids: string[];
  resourceType: string;
  academicYear: number;
}

const fetchComments = async (requestData): Promise<ApiResponse<CommentPaginationResponse>> => {
  const params = {
    params: {
      resourceType: requestData.resourceType,
      resourceUuid: requestData.resourceUuid,
      cursor: requestData.cursor,
      pageSize: requestData.pageSize,
      academicYear: requestData.academicYear,
      teamId: requestData.teamId,
    },
  };
  return await httpClient.get(`/apis/comment/get-comments`, params);
};

const fetchNewComments = async (requestData): Promise<ApiResponse<CommentPaginationResponse>> => {
  const params = {
    params: {
      resourceType: requestData.resourceType,
      resourceUuid: requestData.resourceUuid,
      cursor: requestData.cursor,
      academicYear: requestData.academicYear,
      teamId: requestData.teamId,
    },
  };
  return await httpClient.get(`/apis/comment/get/new-comments`, params);
};

const fetchCommentSummary = async (requestData): Promise<ApiResponse<CommentSummaryResponse>> => {
  const params = {
    params: {
      resourceType: requestData.resourceType,
      resourceUuid: requestData.resourceUuid,
      academicYear: requestData.academicYear,
      teamId: requestData.teamId,
    },
  };
  return await httpClient.get(`/apis/comment/get-summary`, params);
};

const fetchCommentSummaryLatest = async (
  requestData: FetchCommentSummaryLatestParams,
): Promise<ApiResponse<Record<string, CommentSummaryLatestResponse>>> => {
  const params = {
    params: {
      resourceType: requestData.resourceType,
      resourceUuids: requestData.resourceUuids,
      academicYear: requestData.academicYear,
    },
  };
  return await httpClient.get(`/apis/comment/get-summary-latest`, params);
};

const checkNewComments = async (requestData): Promise<ApiResponse<boolean>> => {
  const params = {
    params: {
      resourceType: requestData.resourceType,
      resourceUuid: requestData.resourceUuid,
      cursor: requestData.cursor,
      academicYear: requestData.academicYear,
      teamId: requestData.teamId,
    },
  };
  return await httpClient.get(`/apis/comment/check/new-comments`, params);
};

const checkExistComments = (requestData) => {
  const params = {
    params: {
      resourceType: requestData.resourceType,
      resourceUuid: requestData.resourceUuid,
      includeDeleted: requestData.includeDeleted,
      academicYear: requestData.academicYear,
      teamId: requestData.teamId,
    },
  };
  return httpClient.get(`/apis/comment/check/exist-comments`, params);
};

const createComment = async (requestData): Promise<ApiResponse<CommentResponse>> => {
  return await httpClient.post(`/apis/comment/create`, requestData, {
    headers: {
      "X-With-Richtext": true,
    },
  });
};

const editComment = async (requestData): Promise<ApiResponse<CommentResponse>> => {
  return await httpClient.post(`/apis/comment/edit`, requestData, {
    headers: {
      "X-With-Richtext": true,
    },
  });
};

const softDeleteComment = async (requestData): Promise<ApiResponse<CommentResponse>> => {
  return await httpClient.post(`/apis/comment/softdelete`, requestData);
};

// TODO: [Google] to review the usage
const clearPermissionQuestionAnswerGoogleDocumentComment = async (
  requestData,
): Promise<ApiResponse<unknown>> => {
  return await httpClient.post(`/apis/comment/clear-permission`, requestData, {
    headers: {
      "X-With-Richtext": true,
    },
  });
};

const fetchLastComment = async (requestData): Promise<ApiResponse<CommentResponse>> => {
  const params = {
    params: {
      resourceType: requestData.resourceType,
      resourceUuid: requestData.resourceUuid,
      academicYear: requestData.academicYear,
    },
  };
  return await httpClient.get(`/apis/comment/latest`, params);
};

const fetchFirstComment = (requestData) => {
  const params = {
    params: {
      resourceUuid: requestData.resourceUuid,
      resourceType: requestData.resourceType,
      academicYear: requestData.academicYear,
      teamId: requestData.teamId,
    },
  };
  return httpClient.get(`/apis/comment/first`, params);
};

export default {
  fetchComments,
  fetchNewComments,
  fetchCommentSummary,
  fetchCommentSummaryLatest,
  checkNewComments,
  checkExistComments,
  createComment,
  editComment,
  softDeleteComment,
  clearPermissionQuestionAnswerGoogleDocumentComment,
  fetchLastComment,
  fetchFirstComment,
};
